/**
 * Caixinha de teste dos efeitos especiais (Personalizar dados → "Ver"): joga um dado que cai no
 * resultado da regra, com o efeito dela, usando a mesma física e a mesma cena da mesa.
 *
 * O canvas nasce no primeiro "Ver" e fica montado enquanto o diálogo está aberto. Sem WebGL, a
 * caixinha aponta para o aviso do topo de Dados, que tem os passos; se a cena quebrar, ela avisa que o
 * 3D falhou. O resto das Configurações continua.
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { DiceEffectId } from '../shared.js';
import { useApp } from '../store.js';
import type { StagedRoll } from './DiceScene.js';
import { hasWebGL, SceneBoundary } from './sceneGuard.js';
import { stageDice } from './stage.js';
import { tableView } from './table.js';
import { loadDiceTexture, settleWithin } from './texture.js';

const DiceScene = lazy(() => import('./DiceScene.js'));

/** Um teste: o dado, o valor em que ele cai e o efeito (null: sem efeito). `nonce` muda a cada clique. */
export type PreviewRequest = { sides: number; value: number; effect: DiceEffectId | null; nonce: number };

/** Quanto o teste espera pela textura (ms); depois, joga só com as cores. */
const TEXTURE_WAIT_MS = 1500;

/** `texture`: a referência da imagem dos meus dados (`rpgdesk-file:<id>`), ou null; `textureScale`, o zoom dela. */
export function DicePreview({
  request,
  body,
  ink,
  texture,
  textureScale,
  idleText,
  no3DText,
  failedText,
  className = 'rd-dicecfg__stage',
  onSettled,
}: {
  request: PreviewRequest | null;
  body: string;
  ink: string;
  texture: string | null;
  textureScale: number;
  idleText: string;
  /** sem WebGL: os passos estão no aviso do topo de Dados (config/No3DBanner.tsx) */
  no3DText: string;
  /** a cena quebrou com o WebGL presente */
  failedText: string;
  /** a caixinha de quem usa (o site tem a dele) */
  className?: string;
  /** o dado parou no valor */
  onSettled?: () => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [staged, setStaged] = useState<StagedRoll[]>([]);
  // a cena quebrou: os próximos testes nem tentam
  const [failed, setFailed] = useState(false);
  const lighting = useApp((s) => s.settings.dice3DLighting);
  // o WebGL é conferido uma vez por página (o aviso do topo de Dados já pergunta ao abrir)
  const no3D = failed || (request !== null && !hasWebGL());

  useEffect(() => {
    const box = boxRef.current;
    if (!request || !box || no3D) return;
    let alive = true;
    void (async () => {
      try {
        const image = texture ? loadDiceTexture(texture) : null;
        const [physics, sound] = await Promise.all([import('./simulation.js'), import('./diceSound.js'), import('./DiceScene.js')]);
        await physics.initPhysics();
        const { settings } = useApp.getState();
        const volume = settings.soundRolls ? settings.masterVolume : 0;
        if (volume > 0) await sound.loadDiceSounds();
        const atlasImage = image ? await settleWithin(image, TEXTURE_WAIT_MS, null) : null;
        if (!alive) return;
        // joga de baixo, no meio: o dado atravessa a caixinha e para longe das bordas, quase sempre
        const view = tableView(box.clientWidth, box.clientHeight, { x: box.clientWidth * 0.5, y: box.clientHeight * 0.9 });
        const dice = stageDice([{ sides: request.sides, value: request.value }]);
        // a caixinha é baixa: um dado que para na borda fica cortado e esconde o efeito. Entre alguns
        // lançamentos, fica o que para mais perto do meio (a física de um dado só é rápida)
        let best: { id: string; seed: number; recording: ReturnType<typeof physics.simulateRoll>; off: number } | null = null;
        for (let attempt = 0; attempt < 8; attempt += 1) {
          const id = `preview-${request.nonce}-${attempt}`;
          const seed = physics.seedFromRoll(id);
          const recording = physics.simulateRoll(dice, view, seed);
          const track = recording.dice[0]!;
          const last = (recording.frames - 1) * 3;
          // longe do meio, medido em proporção da caixinha (a altura conta igual à largura)
          const off = Math.max(Math.abs(track.positions[last]!) / view.halfWidth, Math.abs(track.positions[last + 2]!) / view.halfDepth);
          if (!best || off < best.off) best = { id, seed, recording, off };
          if (off < 0.45) break;
        }
        const { id, seed, recording } = best!;
        const effect = request.effect;
        const effects = effect ? dice.map((_, die) => ({ die, effect, value: request.value })) : [];
        // um teste por vez: o novo toma o lugar do anterior
        setStaged([{ id, recording, view, body, ink, texture: atlasImage, textureScale, volume, stagedAt: performance.now(), effects, seed, startedAgo: 0 }]);
      } catch {
        // sem física ou sem 3D: a caixinha fica vazia
      }
    })();
    return () => {
      alive = false;
    };
    // as cores e a textura entram no próximo teste, sem repetir este
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const done = useCallback((id: string) => setStaged((list) => list.filter((roll) => roll.id !== id)), []);
  const settled = useCallback(() => onSettled?.(), [onSettled]);
  const sceneFailed = useCallback(() => {
    setFailed(true);
    setStaged([]);
  }, []);

  return (
    <div
      ref={boxRef}
      className={className}
      data-dice-effects={staged.flatMap((r) => r.effects.map((e) => e.effect)).join(' ') || undefined}
      data-dice-texture={staged.find((r) => r.texture)?.textureScale.toString()}
    >
      {request && !no3D ? (
        <SceneBoundary onError={sceneFailed}>
          <Suspense fallback={null}>
            <DiceScene rolls={staged} lighting={lighting} onSettled={settled} onDone={done} />
          </Suspense>
        </SceneBoundary>
      ) : (
        <span className="rd-dicecfg__stageHint">{no3D ? (failed ? failedText : no3DText) : idleText}</span>
      )}
    </div>
  );
}
